import { SifaTaetigkeit, TaetigkeitArt } from '../../services/sifa/betreuungTypes';
import type { NeueTaetigkeit } from '../../services/sifa/sifaBetreuungService';
import { artLabel, gruppiereJeArt } from '../../services/sifa/taetigkeitenLogic';

/**
 * Die Regeln der Taetigkeitsnachweis-Oberflaeche — rein, ohne DOM. Summen,
 * Soll/Ist, Dauer-Formatierung und -Parsen liegen in
 * services/sifa/taetigkeitenLogic.ts (Paket C); hier steht nur, was die
 * Oberflaeche zusaetzlich braucht: Jahresauswahl, Sortierung, Verteilung
 * je Art fuer die kleine Grafik, Dialog-Entwurf, Dateiname, Nutzername.
 */

export { artLabel };

/** Auswahlliste der Jahre: das laufende zuerst, dann rueckwaerts. */
export const jahresListe = (aktuellesJahr: number, anzahl = 5): number[] =>
  Array.from({ length: Math.max(1, anzahl) }, (_, i) => aktuellesJahr - i);

/** Juengstes Datum zuerst; gleicher Tag: zuletzt erfasst zuerst. */
export const sortiereTaetigkeiten = (liste: SifaTaetigkeit[]): SifaTaetigkeit[] =>
  [...liste].sort((a, b) => b.datum.localeCompare(a.datum) || (b.createdAt || '').localeCompare(a.createdAt || ''));

/** Volltextsuche ueber Titel, Beschreibung, Bezug, Wer und Art-Label — alle Woerter muessen vorkommen. */
export const sucheTaetigkeiten = (liste: SifaTaetigkeit[], suche: string): SifaTaetigkeit[] => {
  const woerter = (suche || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return liste;
  return liste.filter((t) => {
    const text = `${t.titel} ${t.beschreibung || ''} ${t.bezug || ''} ${t.durchgefuehrtVon || ''} ${artLabel(t.art)}`.toLowerCase();
    return woerter.every((w) => text.includes(w));
  });
};

export interface ArtAnteil {
  art: TaetigkeitArt;
  label: string;
  minuten: number;
  anzahl: number;
  /** Anteil an der Gesamtsumme in ganzen Prozent (0 bei leerer Liste). */
  prozent: number;
}

/** Verteilung je Art (gruppiereJeArt, groesste zuerst) plus Anteil an der Summe fuer die kleine Grafik. */
export const verteilungJeArt = (liste: SifaTaetigkeit[]): ArtAnteil[] => {
  const gruppen = gruppiereJeArt(liste);
  const gesamt = gruppen.reduce((s, g) => s + g.minuten, 0);
  return gruppen.map((g) => ({ ...g, prozent: gesamt > 0 ? Math.round((g.minuten / gesamt) * 100) : 0 }));
};

/** Anteil in ganzen Prozent, auf 100 gedeckelt — fuer den Soll/Ist-Balken. */
export const balkenProzent = (istMinuten: number, sollStunden: number | null | undefined): number => {
  if (!sollStunden || sollStunden <= 0) return 0;
  return Math.min(100, Math.round((istMinuten / (sollStunden * 60)) * 100));
};

// ─── Dialog-Entwurf ─────────────────────────────────────────────────────────

export interface TaetigkeitEntwurf {
  /** ISO-Datum. */
  datum: string;
  /** Freitext „1:30" oder „90" — parseDauer (Paket C) macht Minuten daraus. */
  dauer: string;
  art: TaetigkeitArt;
  titel: string;
  beschreibung: string;
  bezug: string;
  durchgefuehrtVon: string;
}

export const leererTaetigkeitEntwurf = (heuteIso: string, durchgefuehrtVon: string | null): TaetigkeitEntwurf => ({
  datum: heuteIso,
  dauer: '',
  art: 'beratung',
  titel: '',
  beschreibung: '',
  bezug: '',
  durchgefuehrtVon: durchgefuehrtVon || '',
});

export const entwurfAusTaetigkeit = (t: SifaTaetigkeit, dauerText: string): TaetigkeitEntwurf => ({
  datum: t.datum,
  dauer: dauerText,
  art: t.art,
  titel: t.titel,
  beschreibung: t.beschreibung || '',
  bezug: t.bezug || '',
  durchgefuehrtVon: t.durchgefuehrtVon || '',
});

/** Fehlerliste; leer = speicherbar. `minuten` ist das Ergebnis von parseDauer (null = unlesbar). */
export const pruefeTaetigkeitEntwurf = (e: TaetigkeitEntwurf, minuten: number | null): string[] => {
  const fehler: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(e.datum)) fehler.push('Das Datum fehlt.');
  if (!e.titel.trim()) fehler.push('Ein Titel ist Pflicht.');
  if (minuten === null || !Number.isFinite(minuten) || minuten <= 0) fehler.push('Dauer als „1:30" oder in Minuten („90") eingeben.');
  return fehler;
};

export const entwurfZuNeueTaetigkeit = (e: TaetigkeitEntwurf, minuten: number): NeueTaetigkeit => ({
  datum: e.datum,
  dauerMinuten: Math.round(minuten),
  art: e.art,
  titel: e.titel.trim(),
  beschreibung: e.beschreibung.trim() || null,
  bezug: e.bezug.trim() || null,
  durchgefuehrtVon: e.durchgefuehrtVon.trim() || null,
});

/** Abgerechnete Zeilen (gebuchtAm) sind Beleg und werden nicht geloescht. */
export const darfLoeschen = (t: Pick<SifaTaetigkeit, 'gebuchtAm'>): boolean => !t.gebuchtAm;

/** Dateiname des PDF: Taetigkeitsnachweis_2026_26051016.pdf. */
export const nachweisDateiname = (jahr: number, projektNummer: string | null | undefined): string => {
  const nr = (projektNummer || '').replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '');
  return `Taetigkeitsnachweis_${jahr}${nr ? `_${nr}` : ''}.pdf`;
};

/** Anzeigename des angemeldeten Nutzers wie im UnterweisungModul: Vor- und Nachname, sonst E-Mail. */
export const nutzerName = (profil: { first_name?: string | null; last_name?: string | null; email?: string | null } | null | undefined): string | null => {
  if (!profil) return null;
  const name = `${profil.first_name || ''} ${profil.last_name || ''}`.trim();
  return name || (profil.email || '').trim() || null;
};
